import { memo } from 'react';
import { Input, Label } from '@flote/ui';
import { Clock } from 'lucide-react';
import type { TimeWindowState } from '../hooks';

interface TimeWindowInputProps {
  timeWindow: TimeWindowState;
  onTimeWindowChange: (field: keyof TimeWindowState, value: string) => void;
  disabled?: boolean;
  idPrefix?: string;
  afterLabel?: string;
  beforeLabel?: string;
  afterDescription?: string;
  beforeDescription?: string;
  title?: string;
}

export const TimeWindowInput = memo<TimeWindowInputProps>(({
  timeWindow,
  onTimeWindowChange,
  disabled = false,
  idPrefix = '',
  afterLabel = 'Complete After',
  beforeLabel = 'Complete Before',
  afterDescription = 'Earliest completion time',
  beforeDescription = 'Deadline for completion',
  title = 'Time Window',
}) => {
  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 text-sm font-medium">
        <Clock className="h-4 w-4 text-muted-foreground" />
        {title}
        <span className="text-xs font-normal text-muted-foreground">(optional)</span>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}completeAfter`}>{afterLabel}</Label>
          <Input
            id={`${idPrefix}completeAfter`}
            type="datetime-local"
            value={timeWindow.completeAfter}
            onChange={(e) => onTimeWindowChange('completeAfter', e.target.value)}
            disabled={disabled}
          />
          {afterDescription && (
            <p className="text-xs text-muted-foreground">{afterDescription}</p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor={`${idPrefix}completeBefore`}>{beforeLabel}</Label>
          <Input
            id={`${idPrefix}completeBefore`}
            type="datetime-local"
            value={timeWindow.completeBefore}
            onChange={(e) => onTimeWindowChange('completeBefore', e.target.value)}
            disabled={disabled}
            min={timeWindow.completeAfter}
          />
          {beforeDescription && (
            <p className="text-xs text-muted-foreground">{beforeDescription}</p>
          )}
        </div>
      </div>
    </div>
  );
});

TimeWindowInput.displayName = 'TimeWindowInput';
